<template lang="pug">
b-card
  validation-observer(ref="form", tag="form")
    b-form
      b-row
        // Nombre
        b-col(md="6")
          b-form-group(:label="$t('biblioteca.nombre')", label-for="nombre")
            validation-provider(:name="$t('biblioteca.nombre')", rules="required", v-slot="{ errors }")
              b-form-input#nombre(v-model="data.nombre", trim, :placeholder="$t('biblioteca.placeholder_nombre')")
              small(class="text-danger") {{ errors[0] }}

        // Tipo de Documento
        b-col(md="6")
          b-form-group(:label="$t('biblioteca.tipoDocumento')", label-for="tipoDocumento")
            validation-provider(:name="$t('biblioteca.tipoDocumento')", rules="required", v-slot="{ errors }")
              v-select(
                v-model="data.tipoDocumento",
                :options="tipoDocumentoOptions",
                :reduce="opt => opt.value",
                label="title",
                :placeholder="$t('biblioteca.placeholder_tipo')"
              )
              small(class="text-danger") {{ errors[0] }}

      b-row
        // Temática
        b-col(md="6")
          b-form-group(:label="$t('biblioteca.tematica')", label-for="tematica")
            b-form-input#tematica(v-model="data.tematica", trim, :placeholder="$t('biblioteca.placeholder_tematica')")

        // Responsable
        b-col(md="6")
          b-form-group(:label="$t('biblioteca.responsable')", label-for="responsable")
            validation-provider(:name="$t('biblioteca.responsable')", rules="required", v-slot="{ errors }")
              v-select(
                v-model="data.responsable",
                :options="workersTotal",
                :reduce="worker => worker.value",
                :selectable="worker => !worker.deleted",
                label="title",
                :placeholder="$t('biblioteca.placeholder_responsable')"
              )
              small(class="text-danger") {{ errors[0] }}

      b-row
        // Clientes (selección múltiple)
        b-col(md="6")
          b-form-group(:label="$t('biblioteca.clientes')", label-for="clientes")
            v-select(
              v-model="data.clientes",
              :options="companies",
              :reduce="company => company.value",
              label="title",
              multiple,
              :placeholder="$t('biblioteca.placeholder_clientes')"
            )

        // Fecha Próxima Revisión
        b-col(md="6")
          b-form-group(:label="$t('biblioteca.fechaProximaRevision')", label-for="fechaProximaRevision")
            b-form-datepicker(v-model="data.fechaProximaRevision", :locale="locale", :placeholder="$t('biblioteca.placeholder_fecha')")

      b-row
        // Observaciones
        b-col(md="12")
          b-form-group(:label="$t('biblioteca.observaciones')", label-for="observaciones")
            b-form-textarea(
              v-model="data.observaciones",
              :placeholder="$t('biblioteca.placeholder_observaciones')",
              rows="3"
            )

      // File upload (only for new documents)
      b-row(v-if="showFileUpload")
        b-col(md="6")
          b-form-group(:label="$t('label.document')", label-for="archivo")
            b-button.mr-1(type="button", variant="info", @click="activateFileSelector")
              feather-icon(icon="UploadIcon", class="mr-50")
              | {{ $t('message.upload') }}
            input(
              type="file",
              ref="fileSelector",
              style="display: none",
              :accept="inputFileTypes",
              @change="changeFileSelected"
            )
            small.ml-1(v-if="documentFile.name")
              feather-icon(icon="FileIcon", class="mr-25")
              | {{ documentFile.name }}
            small(v-else-if="fileRequired", class="text-danger d-block mt-50")
              | {{ $t('biblioteca.file_required') }}

      b-row.mt-2
        b-col.text-right(cols="12")
          b-button(variant="success", :disabled="isSubmitting", @click="validateAndSubmit")
            | {{ $t('message.submit') }}

      b-row(v-if="isSubmitting")
        b-col.text-right(cols="12")
          div.mt-1
            | {{ $t('biblioteca.uploading') }}

  //- Audit Log Table
  audit-log-table(
    v-if="isEdit && data"
    :history="data.history || []"
    :field-translations="{\
      nombre: $t('biblioteca.nombre'),\
      tipoDocumento: $t('biblioteca.tipoDocumento'),\
      tematica: $t('biblioteca.tematica'),\
      responsable: $t('label.responsible'),\
      clientes: $t('biblioteca.clientes'),\
      observaciones: $t('label.observations'),\
      fechaProximaRevision: $t('biblioteca.fechaProximaRevision')\
    }"
  )
</template>

<script>
import vSelect from 'vue-select'
import {
  ref, toRefs, onMounted, computed,
} from '@vue/composition-api/dist/vue-composition-api'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { required } from '@validations'
import useNotifications from '@/composables/useNotifications'
import AuditLogTable from '@/components/AuditLogTable.vue'
import i18n from '@/libs/i18n'
import useCommon from '@/views/organization/useCommon'
import { inputFileTypes } from '@/constants'

export default {
  name: 'LibraryForm',

  components: {
    vSelect,
    ValidationObserver,
    ValidationProvider,
    AuditLogTable,
  },

  props: {
    data: {
      type: Object,
      required: true,
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    showFileUpload: {
      type: Boolean,
      default: true,
    },
    fileRequired: {
      type: Boolean,
      default: true,
    },
  },

  setup(props, { emit }) {
    const {
      getWorkersTotalForDropDown, workersTotal, getCompaniesForDropDown, companies,
    } = useCommon()
    const { showErrorMessage } = useNotifications()
    const { default_language } = JSON.parse(localStorage.getItem('clientData') || '{}')
    const locale = default_language || 'es'
    const form = ref(null)
    const fileSelector = ref(null)
    const documentFile = ref({})

    const tipoDocumentoOptions = computed(() => [
      { value: 'Política', title: i18n.t('biblioteca.tipo_politica') },
      { value: 'Procedimiento', title: i18n.t('biblioteca.tipo_procedimiento') },
      { value: 'Manual', title: i18n.t('biblioteca.tipo_manual') },
      { value: 'Instructivo', title: i18n.t('biblioteca.tipo_instructivo') },
      { value: 'Formato', title: i18n.t('biblioteca.tipo_formato') },
      { value: 'Otro', title: i18n.t('biblioteca.tipo_otro') },
    ])

    const activateFileSelector = () => {
      fileSelector.value.click()
    }

    const changeFileSelected = e => {
      documentFile.value = e.target.files[0]
    }

    const validateAndSubmit = () => {
      form.value.validate().then(success => {
        if (success) {
          if (props.fileRequired && props.showFileUpload && !documentFile.value.name) {
            showErrorMessage(i18n.t('biblioteca.file_required'))
            return
          }
          emit('submit', toRefs(props.data), documentFile.value)
        } else {
          showErrorMessage(i18n.t('message.requiredFieldsIncomplete'))
        }
      })
    }

    onMounted(() => {
      getWorkersTotalForDropDown()
      getCompaniesForDropDown()
    })

    return {
      required,
      form,
      fileSelector,
      documentFile,
      activateFileSelector,
      changeFileSelected,
      validateAndSubmit,
      tipoDocumentoOptions,
      workersTotal,
      companies,
      inputFileTypes,
      locale,
    }
  },
}
</script>
